"use client";

import { Dialog, DialogContent } from "design-system/dialog";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useCanCreateEmbeddedWallet } from "@/lib/hooks/flags/use-can-create-embedded-wallet";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { LoadingTurnkeyView } from "@/subsystems/access/turnkey/components/views/loading-turnkey-view";

// The flow pulls in the Turnkey SDK; load it only when the dialog first opens, like the auth dialog.
const InviteSignerFlow = dynamic(() => import("./invite-signer-flow").then((mod) => mod.InviteSignerFlow), {
  loading: () => <LoadingTurnkeyView />,
  ssr: false,
});

export function InviteSignerDialog({ dialogControls }: { dialogControls: UseDialogReturn<void> }) {
  const enabled = useCanCreateEmbeddedWallet();
  // The flow locks the modal while the Turnkey writes run, so a close can't leave a half-added signer.
  const [locked, setLocked] = useState(false);
  // Never unmount an open dialog on a transient flag read; see CreateEmbeddedWalletDialog.
  if (!enabled && !dialogControls.props.open) return null;

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open && locked) return;
        dialogControls.props.onOpenChange(open);
      }}
      open={dialogControls.props.open}
    >
      <DialogContent
        className="sm:max-w-xl"
        onEscapeKeyDown={(event) => {
          if (locked) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (locked) event.preventDefault();
        }}
      >
        {dialogControls.props.open ? (
          // Standalone dialog: "View Team" may leave for the Team page, dismissing this dialog first.
          <InviteSignerFlow
            allowNavigateAway
            onClose={dialogControls.dismiss}
            onLockedChange={setLocked}
            onNavigateAway={dialogControls.dismiss}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
